import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormTextarea from '../../../components/form/FormTextarea';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormToggle from '../../../components/form/FormToggle';

import {
  aiAgentAdd,
  aiAgentUpdate,
  aiAgentDetailsGetById,
  useVoiceProvidersOptions,
  voiceListGet,
  useModalProviderOptions,
  useSttProviderOptions
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormTags from '../../../components/form/FormTags';
import AIAgentSettingsPage from './AIAgentSettingsPage';
import AdminHeader from '../../../components/header/AdminHeader';
import { useNavigate, useParams } from 'react-router-dom';

// ---------------- OPTIONS ----------------

const CALL_TYPE_OPTIONS = [
  { label: 'Incoming', value: 'Incoming' },
  { label: 'Outgoing', value: 'Outgoing' }
];

const DEEPGRAM_MODEL_OPTIONS = [
  { label: 'Nova-3', value: 'nova-3' },
  { label: 'Nova-2', value: 'nova-2' }
];

const WEB_SEARCH_PROVIDER_OPTIONS = [
  { label: 'DuckDuckGo', value: 'DuckDuckGo' }
  // { label: 'OpenAI', value: 'OpenAI' }
];

const INCLUDE_OPTIONS = [
  { label: 'Summary', value: 'summary' },
  { label: 'Extracted Variables', value: 'extracted_variables' },
  { label: 'Full Conversation', value: 'fullConversation' },
  { label: 'Sentiment', value: 'sentiment' }
];

const AddEditAIAgent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const isEdit = Boolean(agentId);

  const voiceProvidersOptions = useVoiceProvidersOptions();
  const modalProviderOptions = useModalProviderOptions();
  const sttProviderOptions = useSttProviderOptions();

  const audioRef = useRef(null);

  const [voiceList, setVoiceList] = useState([]);
  const [voiceListLoading, setVoiceListLoading] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);

  const initialValues = {
    name: '',
    welcome_message: '',
    call_type: 'Incoming',

    context_breakdown: [{ title: '', body: '', is_enabled: true }],

    transcriber: {
      provider: '',
      model: '',
      silence_timeout_ms: '',
      numerals: false,
      punctuate: true,
      smart_format: true,
      diarize: false
    },

    model: {
      model: '',
      temperature: 0.7
    },

    voice: {
      provider: '',
      voice_id: ''
    },

    web_search: {
      enabled: false,
      provider: 'DuckDuckGo'
    },

    post_call_actions: {
      email: {
        enabled: false,
        recipients: [''],
        include: [],
        extracted_variables: [{ key: '', prompt: '' }]
      },
      webhook: {
        enabled: false,
        url: '',
        include: [],
        extracted_variables: [{ key: '', prompt: '' }]
      }
    },

    filler: {
      enabled: false,
      after_sec: 1,
      fillers: ['']
    },

    transfer: {
      enabled: false,
      transfer_options: [
        {
          number: '',
          backup_numbers: [''],
          transfer_condition: '',
          transfer_message: ''
        }
      ]
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),

    welcome_message: Yup.string().required('Welcome message is required'),

    call_type: Yup.string().required('Call type is required'),

    context_breakdown: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().required('Context title is required'),

        body: Yup.string().required('Context body is required'),

        is_enabled: Yup.boolean()
      })
    ),

    transcriber: Yup.object({
      provider: Yup.string().required('Transcriber provider is required'),

      model: Yup.string().required('Transcriber model is required'),

      silence_timeout_ms: Yup.number().typeError('Silence timeout must be a number').nullable(),

      numerals: Yup.boolean(),
      punctuate: Yup.boolean(),
      smart_format: Yup.boolean(),
      diarize: Yup.boolean()
    }),

    model: Yup.object({
      model: Yup.string().required('Model selection is required'),

      temperature: Yup.number()
        .typeError('Temperature must be a number')
        .min(0, 'Temperature must be at least 0')
        .max(1, 'Temperature must be at most 1')
    }),

    voice: Yup.object({
      provider: Yup.string().required('Voice provider is required'),

      voice_id: Yup.string().required('Voice ID is required')
    }),

    web_search: Yup.object({
      enabled: Yup.boolean(),
      provider: Yup.string()
    }),

    post_call_actions: Yup.object({
      email: Yup.object({
        enabled: Yup.boolean(),

        recipients: Yup.array().when('enabled', {
          is: true,
          then: (schema) =>
            schema
              .of(
                Yup.string()
                  .email('Recipient email must be valid')
                  .required('Recipient email is required')
              )
              .min(1, 'At least one recipient email is required')
        }),

        extracted_variables: Yup.array().when('enabled', {
          is: true,
          then: (schema) =>
            schema.of(
              Yup.object().shape({
                key: Yup.string().required('Email variable key is required'),

                prompt: Yup.string().required('Email variable prompt is required')
              })
            )
        })
      }),

      webhook: Yup.object({
        enabled: Yup.boolean(),

        url: Yup.string().when('enabled', {
          is: true,
          then: (schema) =>
            schema.required('Webhook URL is required').url('Webhook URL must be a valid URL')
        }),

        extracted_variables: Yup.array().when('enabled', {
          is: true,
          then: (schema) =>
            schema.of(
              Yup.object().shape({
                key: Yup.string().required('Webhook variable key is required'),
                prompt: Yup.string().required('Webhook variable prompt is required')
              })
            )
        })
      })
    }),

    filler: Yup.object({
      enabled: Yup.boolean(),

      after_sec: Yup.number().when('enabled', {
        is: true,
        then: (schema) =>
          schema
            .typeError('After seconds must be a number')
            .required('After seconds is required')
            .min(0, 'After seconds must be greater than or equal to 0')
      }),

      fillers: Yup.array().when('enabled', {
        is: true,
        then: (schema) =>
          schema
            .of(Yup.string().required('Filler text is required'))
            .min(1, 'At least one filler is required')
      })
    }),

    transfer: Yup.object({
      enabled: Yup.boolean(),

      transfer_options: Yup.array().when('enabled', {
        is: true,
        then: (schema) =>
          schema.of(
            Yup.object().shape({
              number: Yup.string().required('Primary transfer number is required'),

              backup_numbers: Yup.array().of(Yup.string().required('Backup number is required')),

              transfer_condition: Yup.string().nullable(),

              transfer_message: Yup.string().nullable()
            })
          )
      })
    })
  });

  const onSubmit = async () => {
    try {
      const payload = {
        ...values
        // voice: {
        //   ...values.voice,
        //   voice_id: 'JBFqnCBsd6RMkjVDRZzb'
        // }
      };
      if (isEdit) {
        await dispatch(aiAgentUpdate({ id: agentId, ...payload })).unwrap();
        toast.success('Updated successfully');
      } else {
        await dispatch(aiAgentAdd(payload)).unwrap();
        toast.success('Created successfully');
      }
      navigate(-1);
      resetForm();
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    setValues,
    resetForm,
    validateForm
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const mapApiToForm = (data) => {
    if (!data) return initialValues;

    return {
      name: data?.name || '',
      welcome_message: data?.welcome_message || '',
      call_type: data?.bot_call_type || 'Incoming',

      /* ================= CONTEXT BREAKDOWN ================= */
      context_breakdown:
        data?.context_breakdown?.length > 0
          ? data.context_breakdown.map((item) => ({
              title: item.context_title || '',
              body: item.context_body || '',
              is_enabled: !!item.is_enabled
            }))
          : initialValues.context_breakdown,

      /* ================= TRANSCRIBER ================= */
      transcriber: {
        provider: data?.asr_service || '',
        model: data?.asr_deepgram_model || '',
        silence_timeout_ms: data?.silence_timeout ?? '',

        numerals: !!data?.asr_deepgram_numerals,
        punctuate: !!data?.asr_deepgram_punctuate,
        smart_format: !!data?.asr_deepgram_smart_format,
        diarize: !!data?.asr_deepgram_diarize
      },

      /* ================= MODEL ================= */
      model: {
        model: data?.llm_service || '',
        temperature: data?.llm_temperature ?? 0.7
      },

      /* ================= VOICE ================= */
      voice: {
        provider: data?.voice_provider || '',
        voice_id: data?.voice_external_id || ''
      },

      /* ================= WEB SEARCH ================= */
      web_search: {
        enabled: !!data?.enable_web_search,
        provider: data?.web_search_engine || 'DuckDuckGo'
      },

      /* ================= POST CALL ACTIONS ================= */
      post_call_actions: {
        email: {
          enabled:
            data?.post_call_config_ids?.some((item) => item.delivery_method === 'Email') || false,

          recipients: data?.post_call_config_ids
            ?.filter((item) => item.delivery_method === 'Email')
            ?.map((item) => item.destination) || [''],

          include: [],

          extracted_variables: data?.post_call_config_ids
            ?.find((item) => item.delivery_method === 'Email')
            ?.extracted_variables?.map((v) => ({
              key: v.key || '',
              prompt: v.description || ''
            })) || [{ key: '', prompt: '' }]
        },

        webhook: {
          enabled:
            data?.post_call_config_ids?.some((item) => item.delivery_method === 'Webhook') || false,

          url:
            data?.post_call_config_ids?.find((item) => item.delivery_method === 'Webhook')
              ?.webhook_url || '',

          include: [],

          extracted_variables: data?.post_call_config_ids
            ?.find((item) => item.delivery_method === 'Webhook')
            ?.extracted_variables?.map((v) => ({
              key: v.key || '',
              prompt: v.description || ''
            })) || [{ key: '', prompt: '' }]
        }
      },

      /* ================= FILLER ================= */
      filler: {
        enabled: !!data?.is_filler_enable,
        after_sec: data?.filler_after_sec ?? 1,
        fillers: data?.fillers?.length ? data.fillers : ['']
      },

      /* ================= TRANSFER ================= */
      transfer: {
        enabled: !!data?.is_transfer_enabled,

        transfer_options:
          data?.transfer_options?.length > 0
            ? data.transfer_options.map((item) => ({
                number: item.number || '',
                backup_numbers: item.backup_numbers?.length > 0 ? item.backup_numbers : [''],
                transfer_condition: item.transfer_condition || '',
                transfer_message: item.transfer_message || ''
              }))
            : [
                {
                  number: '',
                  backup_numbers: [''],
                  transfer_condition: '',
                  transfer_message: ''
                }
              ]
      }
    };
  };

  useEffect(() => {
    if (isEdit && agentId) {
      dispatch(aiAgentDetailsGetById(agentId))
        .unwrap()
        .then((res) => {
          const mappedValues = mapApiToForm(res?.data);
          setValues(mappedValues);
        });
    }
  }, [isEdit, agentId]);

  const fetchVoice = async () => {
    setVoiceListLoading(true);
    try {
      const payload = {
        provider: values.voice.provider
      };
      const response = await dispatch(voiceListGet(payload)).unwrap();
      if (response && response.success) {
        const newData = response.data.map((item) => ({
          ...item,
          id: item.id ? item.id : item.external_id
        }));
        setVoiceList(newData);
      } else {
        setVoiceList([]);
      }
      setVoiceListLoading(false);
    } catch (error) {
      setVoiceListLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values.voice.provider) {
      fetchVoice();
    } else {
      setVoiceList([]);
    }
  }, [values.voice.provider]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayToggle = async (voice) => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      const audio = audioRef.current;

      if (playingVoiceId === voice.name) {
        audio.pause();
        audio.currentTime = 0;
        setPlayingVoiceId(null);
        return;
      }

      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }

      audio.src = voice.sample_url;

      audio.onended = () => {
        setPlayingVoiceId(null);
      };

      audio.onerror = () => {
        setPlayingVoiceId(null);
        audio.pause();
        audio.currentTime = 0;
        toast.error('Audio not available.');
      };

      await audio.play();
      setPlayingVoiceId(voice.name);
    } catch (error) {
      setPlayingVoiceId(null);
    }
  };

  const addContext = () => {
    const updated = [...values.context_breakdown, { title: '', body: '', is_enabled: true }];
    setFieldValue('context_breakdown', updated);
  };

  const removeContext = (index) => {
    const updated = values.context_breakdown.filter((_, i) => i !== index);
    setFieldValue('context_breakdown', updated);
  };

  const addRecipient = () => {
    const updated = [...values.post_call_actions.email.recipients, ''];
    setFieldValue('post_call_actions.email.recipients', updated);
  };

  const removeRecipient = (index) => {
    const updated = values.post_call_actions.email.recipients.filter((_, i) => i !== index);
    setFieldValue('post_call_actions.email.recipients', updated);
  };

  const addFiller = () => {
    const updated = [...values.filler.fillers, ''];
    setFieldValue('filler.fillers', updated);
  };

  const removeFiller = (index) => {
    const updated = values.filler.fillers.filter((_, i) => i !== index);
    setFieldValue('filler.fillers', updated);
  };

  const addEmailVariable = () => {
    const updated = [
      ...values.post_call_actions.email.extracted_variables,
      { key: '', prompt: '' }
    ];
    setFieldValue('post_call_actions.email.extracted_variables', updated);
  };

  const removeEmailVariable = (index) => {
    const updated = values.post_call_actions.email.extracted_variables.filter(
      (_, i) => i !== index
    );
    setFieldValue('post_call_actions.email.extracted_variables', updated);
  };

  const addWebhookVariable = () => {
    const updated = [
      ...values.post_call_actions.webhook.extracted_variables,
      { key: '', prompt: '' }
    ];
    setFieldValue('post_call_actions.webhook.extracted_variables', updated);
  };

  const removeWebhookVariable = (index) => {
    const updated = values.post_call_actions.webhook.extracted_variables.filter(
      (_, i) => i !== index
    );
    setFieldValue('post_call_actions.webhook.extracted_variables', updated);
  };

  const formatTag = (tag) => {
    const value = tag.split(':')[1] || tag;
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleTransferPhoneChange = (e, fieldPath) => {
    let value = e.target.value;
    value = value.replace(/[^0-9+]/g, '');
    if (value.includes('+')) {
      value = '+' + value.replace(/\+/g, '').replace(/^\+/, '');
    }
    setFieldValue(fieldPath, value);
  };

  const addTransferOption = () => {
    const updated = [
      ...values.transfer.transfer_options,
      {
        number: '',
        backup_numbers: [''],
        transfer_condition: '',
        transfer_message: ''
      }
    ];
    setFieldValue('transfer.transfer_options', updated);
  };

  const removeTransferOption = (index) => {
    const updated = values.transfer.transfer_options.filter((_, i) => i !== index);
    setFieldValue('transfer.transfer_options', updated);
  };

  const addBackupNumber = (optionIndex) => {
    const updated = [...values.transfer.transfer_options[optionIndex].backup_numbers, ''];
    setFieldValue(`transfer.transfer_options[${optionIndex}].backup_numbers`, updated);
  };

  const removeBackupNumber = (optionIndex, numberIndex) => {
    const updated = values.transfer.transfer_options[optionIndex].backup_numbers.filter(
      (_, i) => i !== numberIndex
    );
    setFieldValue(`transfer.transfer_options[${optionIndex}].backup_numbers`, updated);
  };

  return (
    <React.Fragment>
      <AdminHeader
        title={isEdit ? 'Edit AI Agent' : 'Create AI Agent'}
        backBtnName="Back"
        addBtnName={isEdit ? 'Update Agent' : 'Create Agent'}
        isBack
        isAdd
        onBackClick={() => {
          navigate(-1);
          resetForm();
        }}
        form_id="ai-agent-form"
      />
      <AIAgentSettingsPage handleSubmit={handleSubmit} validateForm={validateForm}>
        <div id="basic" className="agent-section">
          <div className="context-card">
            <Row>
              <Col lg="6">
                <FormInput
                  name="name"
                  label="Agent Name"
                  placeholder="Enter AI Agent Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="call_type"
                  label="Call Type"
                  options={CALL_TYPE_OPTIONS}
                  value={values.call_type}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="12">
                <FormTextarea
                  className="callex-mb-0"
                  name="welcome_message"
                  label="Welcome Message"
                  placeholder="Enter Welcome Message"
                  value={values.welcome_message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
          </div>
        </div>

        {/* CONTEXT BREAKDOWN */}
        <div id="context" className="agent-section">
          {values.context_breakdown.map((item, i, array) => (
            <div className="context-card" key={i}>
              <Row className="align-items-center">
                <Col lg="6">
                  <FormInput
                    name={`context_breakdown[${i}].title`}
                    label="Section Title"
                    placeholder="Enter section title"
                    value={item.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name={`context_breakdown[${i}].is_enabled`}
                    label="Enabled"
                    value={String(item.is_enabled)}
                    onChange={() =>
                      setFieldValue(`context_breakdown[${i}].is_enabled`, !item.is_enabled)
                    }
                  />
                </Col>
                <Col lg="3" className="d-flex align-items-center gap-3 pt-2 justify-content-end">
                  {(array?.length > 1 || i !== 0) && (
                    <div className="icon-action danger" onClick={() => removeContext(i)}>
                      <Icon.Trash size={16} />
                    </div>
                  )}

                  {array?.length - 1 === i && (
                    <div className="icon-action info" onClick={() => addContext()}>
                      <Icon.Plus size={16} />
                    </div>
                  )}
                </Col>

                <Col lg="12">
                  <FormTextarea
                    className="callex-mb-0"
                    name={`context_breakdown[${i}].body`}
                    label="Body"
                    placeholder="Enter section content..."
                    value={item.body}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    rows={2}
                  />
                </Col>
              </Row>
            </div>
          ))}
        </div>

        {/* TRANSCRIBER */}
        <div id="transcriber" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="4">
                <FormSelect
                  name="transcriber.provider"
                  label="Provider"
                  placeholder="Select Transcriber Provider"
                  options={sttProviderOptions}
                  value={values.transcriber.provider}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4">
                <FormSelect
                  name="transcriber.model"
                  label="Model"
                  placeholder="Select Model"
                  options={DEEPGRAM_MODEL_OPTIONS}
                  value={values.transcriber.model}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4">
                <FormNumberInput
                  name="transcriber.silence_timeout_ms"
                  label="Silence Timeout (ms)"
                  placeholder="Enter Silence Timeout in milliseconds"
                  value={values.transcriber.silence_timeout_ms}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <Row className="pt-2 align-items-center">
              <Col lg="3">
                <FormToggle
                  name="transcriber.numerals"
                  label="Convert Numbers to Digits"
                  value={String(values.transcriber.numerals)}
                  onChange={() =>
                    setFieldValue('transcriber.numerals', !values.transcriber.numerals)
                  }
                />
              </Col>
              <Col lg="3">
                <FormToggle
                  name="transcriber.punctuate"
                  label="Add Punctuation"
                  value={String(values.transcriber.punctuate)}
                  onChange={() =>
                    setFieldValue('transcriber.punctuate', !values.transcriber.punctuate)
                  }
                />
              </Col>
              <Col lg="3">
                <FormToggle
                  name="transcriber.smart_format"
                  label="Smart Formatting"
                  value={String(values.transcriber.smart_format)}
                  onChange={() =>
                    setFieldValue('transcriber.smart_format', !values.transcriber.smart_format)
                  }
                />
              </Col>
              <Col lg="3">
                <FormToggle
                  name="transcriber.diarize"
                  label="Speaker Diarization"
                  value={String(values.transcriber.diarize)}
                  onChange={() => setFieldValue('transcriber.diarize', !values.transcriber.diarize)}
                />
              </Col>
            </Row>
          </div>
        </div>

        {/* MODEL */}
        <div id="model" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="6">
                <FormSelect
                  name="model.model"
                  className="callex-mb-0"
                  label="Model"
                  placeholder="Select AI Model"
                  options={modalProviderOptions}
                  value={values.model.model}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                  isGroup={true}
                />
              </Col>
              <Col lg="6">
                <FormNumberInput
                  className="callex-mb-0"
                  name="model.temperature"
                  label="Temperature"
                  placeholder="Enter Temperature"
                  value={values.model.temperature}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
          </div>
        </div>

        {/* VOICE */}
        <div id="voice" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="6">
                <FormSelect
                  className="callex-mb-0"
                  name="voice.provider"
                  placeholder="Select Voice Provider"
                  label="Provider"
                  options={voiceProvidersOptions}
                  value={values.voice.provider}
                  onChange={(e) => {
                    if (audioRef.current) {
                      audioRef.current.pause();
                      audioRef.current.currentTime = 0;
                    }
                    setPlayingVoiceId(null);
                    handleChange(e);
                    setFieldValue('voice.voice_id', '');
                  }}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="6">
                <FormInput
                  className="callex-mb-0"
                  name="voice.voice_id"
                  label="Voice ID"
                  disabled
                  placeholder="Select Voice from the list below"
                  value={values.voice.voice_id}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
          </div>
          <div className="context-card">
            {values.voice.provider ? (
              !voiceListLoading && voiceList?.length > 0 ? (
                <Row>
                  {voiceList.map((voice, index) => (
                    <Col lg="6" className="mb-3" key={index}>
                      <div
                        className={`voice-card p-3 rounded border ${
                          values.voice.voice_id === voice.name ? 'selected-card' : ''
                        }`}
                        onClick={() => {
                          setFieldValue('voice.voice_id', voice.name);
                        }}
                        style={{
                          cursor: 'pointer',
                          background: '#ffffff',
                          transition: '0.2s ease'
                        }}>
                        {/* Top Row */}
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-semibold">{voice.display_name || voice.name}</div>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayToggle(voice);
                            }}
                            style={{ cursor: 'pointer' }}>
                            {playingVoiceId === voice.name ? (
                              <div className="icon-action danger">
                                <Icon.Square size={16} className="text-danger" />
                              </div>
                            ) : (
                              <div className="icon-action info">
                                <Icon.Play size={16} className="text-info" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="mt-2">
                          <FormTags tags={voice.tags} formatTag={formatTag} />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="p-0 d-flex justify-content-center align-items-center no-data m-0">
                  {voiceListLoading ? 'Loading Voices...' : 'No voice available'}
                </div>
              )
            ) : null}
          </div>
        </div>

        {/* WEB SEARCH */}
        <div id="websearch" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="6">
                <FormSelect
                  name="web_search.provider"
                  label="Provider"
                  className="callex-mb-0"
                  placeholder="Select Web Search Provider"
                  options={WEB_SEARCH_PROVIDER_OPTIONS}
                  value={values.web_search.provider}
                  onChange={handleChange}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="6">
                <FormToggle
                  className="callex-mb-0"
                  name="web_search.enabled"
                  label="Enable"
                  value={String(values.web_search.enabled)}
                  onChange={() => setFieldValue('web_search.enabled', !values.web_search.enabled)}
                />
              </Col>
            </Row>
          </div>
        </div>

        {/* EMAIL */}
        <div id="email" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="12">
                <FormToggle
                  className="callex-mb-0"
                  name="post_call_actions.email.enabled"
                  label="Enable Email"
                  value={String(values.post_call_actions.email.enabled)}
                  onChange={() =>
                    setFieldValue(
                      'post_call_actions.email.enabled',
                      !values.post_call_actions.email.enabled
                    )
                  }
                />
              </Col>
            </Row>
          </div>

          {values.post_call_actions.email.enabled === true && (
            <>
              <div className="context-card">
                <Row className="py-2 align-items-center">
                  {values.post_call_actions.email.recipients.map((r, i, array) => (
                    <React.Fragment key={i}>
                      <Col lg="4">
                        <FormInput
                          label="Email"
                          name={`post_call_actions.email.recipients[${i}]`}
                          placeholder="Enter Email"
                          value={r}
                          onChange={handleChange}
                          touched={touched}
                          errors={errors}
                        />
                      </Col>
                      <Col lg="2" className="d-flex align-items-center gap-3 pt-2">
                        {(array?.length > 1 || i !== 0) && (
                          <div className="icon-action danger" onClick={() => removeRecipient(i)}>
                            <Icon.Trash size={16} />
                          </div>
                        )}

                        {array?.length - 1 === i && (
                          <div className="icon-action info" onClick={() => addRecipient()}>
                            <Icon.Plus size={16} />
                          </div>
                        )}
                      </Col>
                    </React.Fragment>
                  ))}
                </Row>
                <Row>
                  <Col lg="6">
                    <FormSelect
                      name="post_call_actions.email.include"
                      label="Include"
                      placeholder="Select what to include in the email"
                      options={INCLUDE_OPTIONS}
                      value={values.post_call_actions.email.include}
                      onChange={handleChange}
                      isMulti
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </div>

              <div className="w-100 py-2 px-3 mb-3 rounded-2 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                <div>Extracted Variables</div>
              </div>

              {values.post_call_actions.email.extracted_variables.map((v, i, array) => (
                <div className="context-card" key={i}>
                  <Row className="py-2 align-items-center">
                    <Col lg="6">
                      <FormInput
                        name={`post_call_actions.email.extracted_variables[${i}].key`}
                        label="Variable Key"
                        placeholder="Enter Variable Key"
                        value={v.key}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    <Col
                      lg="6"
                      className="d-flex align-items-center gap-3 pt-2 justify-content-end">
                      {(array?.length > 1 || i !== 0) && (
                        <div className="icon-action danger" onClick={() => removeEmailVariable(i)}>
                          <Icon.Trash size={16} />
                        </div>
                      )}

                      {array?.length - 1 === i && (
                        <div className="icon-action info" onClick={() => addEmailVariable()}>
                          <Icon.Plus size={16} />
                        </div>
                      )}
                    </Col>
                    <Col lg="12">
                      <FormTextarea
                        className="callex-mb-0"
                        name={`post_call_actions.email.extracted_variables[${i}].prompt`}
                        placeholder="Enter prompt to extract this variable from the conversation"
                        label="Extraction Prompt"
                        value={v.prompt}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                        rows={2}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}
        </div>

        {/* WEBHOOK */}
        <div id="webhook" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="12">
                <FormToggle
                  className="callex-mb-0"
                  name="post_call_actions.webhook.enabled"
                  label="Enable Webhook"
                  value={String(values.post_call_actions.webhook.enabled)}
                  onChange={() =>
                    setFieldValue(
                      'post_call_actions.webhook.enabled',
                      !values.post_call_actions.webhook.enabled
                    )
                  }
                />
              </Col>
            </Row>
          </div>

          {values.post_call_actions.webhook.enabled === true && (
            <React.Fragment>
              <div className="context-card">
                <Row className="py-2 align-items-center">
                  <Col lg="6">
                    <FormInput
                      className="callex-mb-0"
                      name="post_call_actions.webhook.url"
                      label="Webhook URL"
                      placeholder="https://your-webhook-url.com"
                      value={values.post_call_actions.webhook.url}
                      onChange={handleChange}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>

                  <Col lg="6">
                    <FormSelect
                      className="callex-mb-0"
                      name="post_call_actions.webhook.include"
                      label="Include"
                      placeholder="Select what to include in the webhook"
                      options={INCLUDE_OPTIONS}
                      value={values.post_call_actions.webhook.include}
                      onChange={handleChange}
                      isMulti
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </div>

              {/* Extracted Variables Header */}
              <div className="w-100 py-2 px-3 mb-3 rounded-2 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                <div>Extracted Variables</div>
              </div>

              {/* Extracted Variables List */}
              {values.post_call_actions.webhook.extracted_variables.map((v, i, array) => (
                <div className="context-card" key={i}>
                  <Row className="py-2 align-items-center">
                    <Col lg="6">
                      <FormInput
                        name={`post_call_actions.webhook.extracted_variables[${i}].key`}
                        label="Variable Key"
                        placeholder="Enter Variable Key"
                        value={v.key}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col
                      lg="6"
                      className="d-flex align-items-center gap-4 pt-2 justify-content-end">
                      {(array?.length > 1 || i !== 0) && (
                        <div
                          className="icon-action danger"
                          onClick={() => removeWebhookVariable(i)}>
                          <Icon.Trash size={16} />
                        </div>
                      )}

                      {array?.length - 1 === i && (
                        <div className="icon-action info" onClick={() => addWebhookVariable()}>
                          <Icon.Plus size={16} />
                        </div>
                      )}
                    </Col>

                    <Col lg="12">
                      <FormTextarea
                        className="callex-mb-0"
                        name={`post_call_actions.webhook.extracted_variables[${i}].prompt`}
                        label="Extraction Prompt"
                        value={v.prompt}
                        onChange={handleChange}
                        placeholder="Enter prompt to extract this variable from the conversation"
                        touched={touched}
                        errors={errors}
                        rows={2}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </React.Fragment>
          )}
        </div>

        {/* FILLER */}
        <div id="filler" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="12">
                <FormToggle
                  className="callex-mb-0"
                  name="filler.enabled"
                  label="Enable Filler"
                  value={String(values.filler.enabled)}
                  onChange={() => setFieldValue('filler.enabled', !values.filler.enabled)}
                />
              </Col>
            </Row>
          </div>

          {values.filler.enabled === true && (
            <React.Fragment>
              <div className="context-card">
                <Row className="py-2 align-items-center">
                  <Col lg="4">
                    <FormNumberInput
                      className="callex-mb-0"
                      name="filler.after_sec"
                      label="After Seconds"
                      placeholder="Enter delay in seconds"
                      value={values.filler.after_sec}
                      onChange={handleChange}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </div>

              {/* Filler Phrases Header */}
              <div className="w-100 py-2 px-3 mb-3 rounded-2 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                <div>Filler Phrases</div>
              </div>

              {/* Filler List */}
              <div className="context-card">
                {values.filler.fillers.map((f, i, array) => (
                  <Row className="py-2 align-items-end" key={i}>
                    <Col lg="10">
                      <FormInput
                        isShowLabel={false}
                        className="callex-mb-0"
                        name={`filler.fillers[${i}]`}
                        label="Filler Text"
                        placeholder="Enter filler phrase"
                        value={f}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col
                      lg="2"
                      className="d-flex align-items-center gap-4 pt-2 justify-content-end">
                      {(array?.length > 1 || i !== 0) && (
                        <div className="icon-action danger" onClick={() => removeFiller(i)}>
                          <Icon.Trash size={16} />
                        </div>
                      )}

                      {array?.length - 1 === i && (
                        <div className="icon-action info" onClick={() => addFiller()}>
                          <Icon.Plus size={16} />
                        </div>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            </React.Fragment>
          )}
        </div>

        {/* TRANSFER */}
        <div id="transfer" className="agent-section">
          <div className="context-card">
            <Row className="py-2 align-items-center">
              <Col lg="12">
                <FormToggle
                  name="transfer.enabled"
                  label="Enable Call Transfer"
                  value={String(values.transfer.enabled)}
                  onChange={() => setFieldValue('transfer.enabled', !values.transfer.enabled)}
                />
              </Col>
            </Row>
          </div>

          {values.transfer.enabled && (
            <>
              {values.transfer.transfer_options.map((option, i, array) => (
                <div className="context-card" key={i}>
                  <Row className="py-2 align-items-center">
                    <Col lg="6">
                      <FormInput
                        name={`transfer.transfer_options[${i}].number`}
                        label="Primary Transfer Number"
                        placeholder="Enter primary phone number"
                        value={option.number}
                        onChange={(e) =>
                          handleTransferPhoneChange(e, `transfer.transfer_options[${i}].number`)
                        }
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col
                      lg="6"
                      className="d-flex align-items-center justify-content-end gap-3 pt-2">
                      {(array.length > 1 || i !== 0) && (
                        <div className="icon-action danger" onClick={() => removeTransferOption(i)}>
                          <Icon.Trash size={16} />
                        </div>
                      )}

                      {array.length - 1 === i && (
                        <div className="icon-action info" onClick={() => addTransferOption()}>
                          <Icon.Plus size={16} />
                        </div>
                      )}
                    </Col>

                    {/* Backup Numbers */}
                    {option.backup_numbers.map((num, j, numArray) => (
                      <React.Fragment key={j}>
                        <Col lg="5">
                          <FormInput
                            name={`transfer.transfer_options[${i}].backup_numbers[${j}]`}
                            label="Backup Number"
                            placeholder="Enter backup number"
                            value={num}
                            onChange={(e) =>
                              handleTransferPhoneChange(
                                e,
                                `transfer.transfer_options[${i}].backup_numbers[${j}]`
                              )
                            }
                            touched={touched}
                            errors={errors}
                          />
                        </Col>

                        <Col lg="1" className="d-flex align-items-center gap-3 pt-2">
                          {(numArray.length > 1 || j !== 0) && (
                            <div
                              className="icon-action danger"
                              onClick={() => removeBackupNumber(i, j)}>
                              <Icon.Trash size={16} />
                            </div>
                          )}

                          {numArray.length - 1 === j && (
                            <div className="icon-action info" onClick={() => addBackupNumber(i)}>
                              <Icon.Plus size={16} />
                            </div>
                          )}
                        </Col>
                      </React.Fragment>
                    ))}

                    <Col lg="12">
                      <FormTextarea
                        name={`transfer.transfer_options[${i}].transfer_condition`}
                        label="Transfer Condition"
                        placeholder="Example: Transfer if customer asks to speak with a human"
                        value={option.transfer_condition}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                        rows={2}
                      />
                    </Col>

                    <Col lg="12">
                      <FormTextarea
                        name={`transfer.transfer_options[${i}].transfer_message`}
                        label="Transfer Message"
                        placeholder="Message agent says before transfer"
                        value={option.transfer_message}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                        rows={2}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}
        </div>
      </AIAgentSettingsPage>
    </React.Fragment>
  );
};

export default AddEditAIAgent;
